// src/hooks/useOrgClaims.js
import { useEffect, useMemo, useState, useCallback } from 'react';
import { onIdTokenChanged } from 'firebase/auth';
import { auth } from '../firebaseConfig';

/**
 * Returns: { user, orgId, role, loading, refreshClaims }
 * - No forced refresh on every render (prevents auth/quota-exceeded).
 * - Call refreshClaims() manually after you change claims server-side.
 */
export default function useOrgClaims() {
  const [state, setState] = useState({
    user: null,
    orgId: null,
    role: null,
    loading: true,
  });

  // Manual, opt-in refresh if you ever need it
  const refreshClaims = useCallback(async () => {
    const u = auth.currentUser;
    if (!u) return;
    await u.getIdToken(true); // force refresh once on demand
    const res = await u.getIdTokenResult();
    setState((s) => ({
      ...s,
      user: u,
      orgId: res.claims?.orgId ?? null,
      role: res.claims?.role ?? null,
      loading: false,
    }));
  }, []);

  useEffect(() => {
    // Single listener; no "true" refresh here
    const unsub = onIdTokenChanged(auth, async (u) => {
      if (!u) {
        setState({ user: null, orgId: null, role: null, loading: false });
        return;
      }
      try {
        const res = await u.getIdTokenResult(); // no force refresh
        setState({
          user: u,
          orgId: res.claims?.orgId ?? null,
          role: res.claims?.role ?? null,
          loading: false,
        });
      } catch {
        // If token fetch fails transiently, still surface the user
        setState((s) => ({ ...s, user: u, loading: false }));
      }
    });
    return () => unsub();
  }, []);

  return useMemo(
    () => ({
      user: state.user,
      orgId: state.orgId,
      role: state.role,
      loading: state.loading,
      refreshClaims,
    }),
    [state, refreshClaims]
  );
}
